/**
 * extract is a shallow extraction utility
 *
 * single key:
 *  extract({ a: 1 }, 'a') -> <number> 1
 *  extract({ a: null }, 'a', 1) -> <number> 1
 *  extract({ a: 1 }, 'b') -> TypeError key does not exist
 *
 * mutli key:
 *  extract({ a: 1, b: '', c: true }, ['a', 'c']) -> <object> { a: 1, c: true }
 */
export function extract<T extends unknown, F extends keyof T>(source: T | undefined, field: F): T[F] | null
export function extract<T extends unknown, F extends keyof T, D extends T[F]>(
    source: T | undefined,
    field: F,
    defaultValue: D,
): D
export function extract<T extends unknown, F extends keyof T>(
    source: T | undefined,
    fields: F[],
): { [K in F]: T[F] | null }
export function extract<T extends unknown, F extends keyof T>(
    source: T | undefined,
    fields: F | F[],
    defaultValue?: T[F],
): T[F] | { [K in F]: T[F] | null } | null {
    const fieldsArr = Array.isArray(fields) ? fields : [fields]
    const response = fieldsArr
        .map((field) => [field, source?.[field] ?? null])
        .reduce((obj, [k, v]) => {
            obj[k] = v
            return obj
        }, {} as any) as { [K in F]: T[F] | null }

    return fieldsArr?.length === 1 ? response[fieldsArr[0]] ?? defaultValue ?? null : response
}

export function hasKey(obj: object, key: string, deep: boolean = false): boolean {
    let keyFound = false

    if (obj !== undefined && obj !== null && typeof obj === 'object') {
        const keys = Object.keys(obj)
        keyFound = keys.includes(key)

        if (!keyFound && deep) {
            for (const _key of keys) {
                const nested = obj[_key]
                if (typeof nested === 'object') {
                    keyFound = hasKey(nested, key, deep)
                    if (keyFound) {
                        break
                    }
                }
            }
        }
    }

    return keyFound
}

function _findKeyPath(obj: object, key: string, deep: boolean = false, _path: string = ''): string | undefined {
    let keyFound = false
    let path: string | undefined = _path

    if (obj !== undefined && obj !== null && typeof obj === 'object') {
        const keys = Object.keys(obj)
        keyFound = keys.includes(key)

        if (keyFound) {
            path = `${path}.${key}`
        } else if (deep) {
            for (const _key of keys) {
                const nested = obj[_key]
                if (typeof nested === 'object') {
                    const pathRgx = new RegExp(`\.${key}$`)
                    const nestedPath = _findKeyPath(nested, key, deep, `${path}.${_key}`)
                    if (!!nestedPath && pathRgx.test(nestedPath)) {
                        path = nestedPath
                        break
                    }
                }
            }
        }
    }

    return !path ? undefined : path.replace(/^\./, '')
}

export function findKeyPath(obj: object, key: string, deep: boolean = false) {
    return _findKeyPath(obj, key, deep)
}

export function isPromise(o: any): boolean {
    return typeof o === 'object' && typeof o.then === 'function'
}

// Polyfill for Object.fromEntries
export function objectFromEntries<T>(entries: Array<[string | number, T]>) {
    let obj: { [key: string]: T } = {}
    for (let [key, value] of entries) {
        obj[key] = value
    }
    return obj
}

/**
 * Niche use-case for mapping Object keys to an Array type.
 *
 * Currently, how the concept of Channels is handled throughout platform, swapping a stateful variable
 * for a data parameter.
 *
 * @param obj
 * @param negativeKeys
 */
export function extractBooleanKeys<T>(obj: T, negativeKeys: boolean = false): (keyof T)[] {
    /**
     *  Explicitly typing because Object.keys returns string[] - can interfere with typings across usages downstream.
     *
     *  Described in Typescript issue response:
     *  "This is intentional. Types in TS are open ended. So keysof will likely be less than all properties you would get at runtime."
     *  https://github.com/Microsoft/TypeScript/issues/12870
     */
    return (Object.keys(obj as any) as (keyof T)[]).filter((key) => (negativeKeys ? !obj[key] : obj[key]))
}

/**
 * Returns key-value object from source1 where the keys exist in source2
 *
 * @param source1
 * @param source2
 */
export function getObjectIntersection(source1: object, source2: object) {
    return objectFromEntries(
        Object.keys(source1)
            .filter((key) => key in source2)
            .map((key) => [key, source1[key]]),
    )
}

export function isJSONObjectType(obj: object): boolean {
    return typeof obj === 'object' && !Array.isArray(obj) && obj !== null
}

/**
 * Simple ordering of object keys including nested object keys / arrays.
 * (Does not order objects within array).
 *
 * @param obj
 */
export const order = (obj: object) => {
    if (obj && typeof obj === 'object') {
        if (Array.isArray(obj)) {
            return obj.map(order)
        }

        const _o = {}
        for (const k of Object.keys(obj).sort()) {
            _o[k] = order(obj[k])
        }
        return _o
    }

    return obj
}

// region --- Deep Comparison ---

/**
 * The result when objects are identical.
 */
type SameResult = {
    areSame: true
}

/**
 * The result when a difference is found.
 */
type DifferentResult = {
    areSame: false
    /**
     * The path to the key that had a discrepancy.
     * e.g., "user.address.street" or "items[2]"
     */
    key: string
    /** The reason for the difference. */
    reason:
        | 'value_mismatch'
        | 'type_mismatch' // e.g., Array vs Object
        | 'missing_in_obj1'
        | 'missing_in_obj2'
    /** The value from the first object. */
    value1?: any
    /** The value from the second object. */
    value2?: any
}

/**
 * A discriminated union for the comparison result.
 */
export type ObjectComparisonResult = SameResult | DifferentResult

// --- The Deep Comparison Function ---

/**
 * Performs a deep, key-by-key comparison of two objects.
 * It recursively checks nested objects and arrays.
 *
 * @returns An ObjectComparisonResult.
 * If `{areSame: true}`, objects are identical.
 * If `{areSame: false}`, the object contains details of the *first* difference found.
 *
 * @remarks The function is not a full exhaustive comparison algorithm and may not handle all edge cases.
 */
export function deepCompareObjects(obj1: any, obj2: any): ObjectComparisonResult {
    // Check for type mismatch at the current level (e.g., Array vs Object)
    const isObj1Array = Array.isArray(obj1)
    const isObj2Array = Array.isArray(obj2)

    if (isObj1Array !== isObj2Array) {
        return {
            areSame: false,
            key: '(root)', // Indicates the mismatch is the items themselves
            reason: 'type_mismatch',
            value1: isObj1Array ? 'Array' : 'Object',
            value2: isObj2Array ? 'Array' : 'Object',
        }
    }

    // Get all unique keys from both objects
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)])

    for (const key of allKeys) {
        const inObj1 = obj1.hasOwnProperty(key)
        const inObj2 = obj2.hasOwnProperty(key)
        const val1 = obj1[key]
        const val2 = obj2[key]

        // Case 1: Key only in obj2 (missing in obj1)
        if (!inObj1 && inObj2) {
            return {
                areSame: false,
                key,
                reason: 'missing_in_obj1',
                value1: undefined,
                value2: val2,
            }
        }

        // Case 2: Key only in obj1 (missing in obj2)
        if (inObj1 && !inObj2) {
            return {
                areSame: false,
                key,
                reason: 'missing_in_obj2',
                value1: val1,
                value2: undefined,
            }
        }

        // Case 3: Key in both, perform deep comparison
        const isVal1Object = typeof val1 === 'object' && val1 !== null
        const isVal2Object = typeof val2 === 'object' && val2 !== null

        // --- Recurse if both values are objects (or arrays) ---
        if (isVal1Object && isVal2Object) {
            const nestedResult = deepCompareObjects(val1, val2)

            if (!nestedResult.areSame) {
                // Difference found in nested object. Prepend the current key to the path.
                const pathKey = isObj1Array
                    ? `${key}[${nestedResult.key}]` // Array path: items[2]
                    : `${key}.${nestedResult.key}` // Object path: user.address

                // Handle the '(root)' key from a type mismatch
                const finalKey = nestedResult.key === '(root)' ? key : pathKey

                return {
                    ...nestedResult,
                    key: finalKey,
                }
            }
        }
        // --- Primitives or non-matching types (e.g., object vs null) ---
        else if (val1 !== val2) {
            return {
                areSame: false,
                key,
                reason: 'value_mismatch',
                value1: val1,
                value2: val2,
            }
        }
    }

    // If the loop finishes, they are the same
    return { areSame: true }
}

// endregion --- Deep Comparison ---
